import DashboardLayout from "@/components/DashboardLayout";
import { ReportMissingSiteButton } from "@/components/customers/ReportMissingSiteButton";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import {
  toastSvrNotificationWarnings,
} from "@/components/site-visits/SvrInventoryTeamFields";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { usePathname, navigate } from "wouter/use-browser-location";
import {
  SITE_VISIT_TOTAL_SLOTS as TOTAL_SLOTS,
  buildArrivalWindowFromSlots,
  leavePeriodSlotRange,
  resolveSiteVisitCalendarPlacement,
  siteVisitSlotLabel as slotLabel,
  siteVisitSlotLabel as slotToTimeLabel,
  slotRangesOverlap,
} from "@shared/siteVisitCalendarWindows";
import { parseValidArrivalWindow } from "@shared/svrSchedulingIntegrity";
import {
  formatSingaporeDate,
  getSingaporeDateKeyFromInstant,
  parseSingaporeDateInput,
  singaporeCalendarRangeBounds,
  singaporeDateKeysEqual,
  singaporeTodayDateKey,
} from "@shared/timezone";
import { addDays, coerceValidDay, startOfWeekMonday } from "@/components/field-calendar/fieldCalendarUtils";

const SLOT_ROW_PX = 40;

const STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-blue-100 border-blue-300 text-blue-800",
  pending_review: "bg-amber-100 border-amber-300 text-amber-800",
  report_sent: "bg-purple-100 border-purple-300 text-purple-800",
  awaiting_payment: "bg-orange-100 border-orange-300 text-orange-800",
  closed: "bg-gray-100 border-gray-300 text-gray-500",
  waived: "bg-gray-100 border-gray-300 text-gray-500",
};

type Customer = {
  id: number;
  name: string;
  contact: string | null;
  email: string | null;
  address: string | null;
  unitNumber: string | null;
  postalCode: string | null;
};

type Assignee = {
  userId: number;
  userName: string;
  role: "technician" | "programmer" | "installer";
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NewSiteVisit() {
  const [, setLocation] = useLocation();
  const pathname = usePathname();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const prefilledCustomerId = params.get("customerId") ? parseInt(params.get("customerId")!) : null;

  // ── Mobile detection ─────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── View mode: "1" | "3" | "7" ───────────────────────────────────────────
  const [viewDays, setViewDays] = useState<1 | 3 | 7>(() => window.innerWidth < 768 ? 1 : 7);

  // ── Calendar navigation ──────────────────────────────────────────────────
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const todayKey = singaporeTodayDateKey();
  const isPastSingaporeDay = (day: Date) => {
    const key = getSingaporeDateKeyFromInstant(day);
    return key != null && key < todayKey;
  };
  const isTodaySingaporeDay = (day: Date) => singaporeDateKeysEqual(day, todayKey);

  // For 1-day and 3-day views, anchor to currentDate directly (Singapore day)
  const viewStart = useMemo(() => {
    if (viewDays === 7) return startOfWeekMonday(currentDate);
    return coerceValidDay(currentDate);
  }, [currentDate, viewDays]);

  const weekStart = useMemo(() => startOfWeekMonday(currentDate), [currentDate]);
  const viewDaysList = useMemo(() => Array.from({ length: viewDays }, (_, i) => addDays(viewStart, i)), [viewStart, viewDays]);
  // Keep weekDays for backward compat with conflict detection
  const weekDays = viewDaysList;
  // Inclusive end: last visible Singapore day (noon-anchored identity for UI).
  const viewEnd = useMemo(() => addDays(viewStart, viewDays - 1), [viewStart, viewDays]);
  const weekEnd = viewEnd;
  const calendarQueryRange = useMemo(
    () => singaporeCalendarRangeBounds(viewStart, viewEnd),
    [viewStart, viewEnd],
  );

  const viewTitle = viewDays === 1
    ? formatSingaporeDate(viewStart, { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    : `${formatSingaporeDate(viewStart, { day: "numeric", month: "short" })} – ${formatSingaporeDate(viewEnd, { day: "numeric", month: "short", year: "numeric" })}`;
  const weekTitle = viewTitle;

  // Navigate by viewDays steps (defined after cancelTap below)

  // ── Existing visits for conflict detection ───────────────────────────────
  const { data: existingVisits = [] } = trpc.siteVisits.calendarVisits.useQuery(
    { startDate: calendarQueryRange.startInclusive, endDate: calendarQueryRange.endExclusive },
    { keepPreviousData: true } as any
  );
  const { data: leaveBlocks = [] } = trpc.siteVisits.teamCalendarLeaveBlocks.useQuery(
    { startDate: calendarQueryRange.startInclusive, endDate: calendarQueryRange.endExclusive },
    { keepPreviousData: true } as any
  );

  const visitsByDay = useMemo(() => {
    const map: Record<number, Array<(typeof existingVisits)[number] & { placement: NonNullable<ReturnType<typeof resolveSiteVisitCalendarPlacement>> }>> = {};
    for (const visit of existingVisits) {
      const placement = resolveSiteVisitCalendarPlacement(visit);
      if (!placement) continue;
      const dayIdx = weekDays.findIndex((day) => singaporeDateKeysEqual(day, placement.placementDate));
      if (dayIdx < 0) continue;
      if (!map[dayIdx]) map[dayIdx] = [];
      map[dayIdx]!.push({ ...visit, placement });
    }
    return map;
  }, [existingVisits, weekDays]);

  const leaveBlocksByDay = useMemo(() => {
    const map: Record<number, typeof leaveBlocks> = {};
    for (const block of leaveBlocks) {
      const dayIdx = weekDays.findIndex((day) => singaporeDateKeysEqual(day, block.leaveDate));
      if (dayIdx < 0) continue;
      if (!map[dayIdx]) map[dayIdx] = [];
      map[dayIdx]!.push(block);
    }
    return map;
  }, [leaveBlocks, weekDays]);

  // ── Drag state ───────────────────────────────────────────────────────────
  const [dragStart, setDragStart] = useState<{ dayIdx: number; slotIdx: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ dayIdx: number; slotIdx: number } | null>(null);
  const isDragging = dragStart !== null;

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlotStart, setSelectedSlotStart] = useState<number>(0);
  const [selectedSlotEnd, setSelectedSlotEnd] = useState<number>(0);

  // ── Customer state ───────────────────────────────────────────────────────
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  // ── New customer modal ───────────────────────────────────────────────────
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustForm, setNewCustForm] = useState({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false });
  const [newCustAddressQuery, setNewCustAddressQuery] = useState("");

  // ── Modal form fields ────────────────────────────────────────────────────
  const [visitType, setVisitType] = useState<"new_install" | "service_visit">("service_visit");
  const [selectedAssignees, setSelectedAssignees] = useState<Assignee[]>([]);
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [preAgreedChargeEnabled, setPreAgreedChargeEnabled] = useState(false);
  const [preAgreedChargeAmount, setPreAgreedChargeAmount] = useState("");

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: customerSites = [] } = trpc.customerSites.list.useQuery(
    { customerId: selectedCustomer?.id ?? 0, includeArchived: false },
    { enabled: !!selectedCustomer }
  );
  const activeCustomerSites = Array.isArray(customerSites) ? customerSites : (customerSites.activeSites ?? []);

  const { data: searchResults } = trpc.customers.searchForServiceVisit.useQuery(
    { query: customerQuery },
    { enabled: customerQuery.length >= 2 && !selectedCustomer }
  );
  const { data: allUsers } = trpc.siteVisits.listStaff.useQuery();

  // Pre-fill customer from URL param
  const { data: prefilledCustomer } = trpc.customers.getByIdForServiceVisit.useQuery(
    { id: prefilledCustomerId! },
    { enabled: !!prefilledCustomerId }
  );
  useEffect(() => {
    if (prefilledCustomer && !selectedCustomer) {
      const c = prefilledCustomer;
      setSelectedCustomer({
        id: c.id,
        name: c.name,
        contact: c.contact ?? null,
        email: c.email ?? null,
        address: c.address ?? null,
        unitNumber: c.unitNumber ?? null,
        postalCode: c.postalCode ?? null,
      });
      setCustomerQuery(c.name);
    }
  }, [prefilledCustomer]);

  // Group users by role for the modal checkboxes
  const technicians = allUsers?.filter(u => u.role === "service" || u.role === "admin") ?? [];
  const programmers = allUsers?.filter(u => u.role === "programming") ?? [];
  const installers = allUsers?.filter(u => u.role === "installer") ?? [];

  const utils = trpc.useUtils();

  // ── Mutations ────────────────────────────────────────────────────────────
  const createVisit = trpc.siteVisits.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Service visit ${data.visitNumber} created`);
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      utils.siteVisits.calendarVisits.invalidate();
      utils.siteVisits.teamCalendarVisits.invalidate();
      utils.siteVisits.teamCalendarLeaveBlocks.invalidate();
      setModalOpen(false);
      navigate(`/site-visits/id/${data.id}`, { state: { from: pathname } });
    },
    onError: (err) => toast.error(err.message || "Failed to create service visit"),
  });

  const createCustomerInline = trpc.customers.create.useMutation({
    onSuccess: async (data) => {
      let selected: Customer = {
        id: data.id,
        name: newCustForm.name.trim(),
        contact: newCustForm.contact.trim() || null,
        email: newCustForm.email.trim() || null,
        address: newCustForm.address.trim() || null,
        unitNumber: newCustForm.unitNumber.trim() || null,
        postalCode: newCustForm.postalCode.trim() || null,
      };
      if ((data as { reusedExisting?: boolean }).reusedExisting) {
        const existing = await utils.customers.getByIdForServiceVisit.fetch({ id: data.id });
        if (existing) {
          selected = {
            id: existing.id,
            name: existing.name,
            contact: existing.contact ?? null,
            email: existing.email ?? null,
            address: existing.address ?? null,
            unitNumber: existing.unitNumber ?? null,
            postalCode: existing.postalCode ?? null,
          };
        }
      }
      setSelectedCustomer(selected);
      setCustomerQuery(selected.name);
      setShowDropdown(false);
      setShowNewCustomerModal(false);
      setNewCustForm({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false });
      setNewCustAddressQuery("");
      utils.customers.list.invalidate();
      if ((data as { reusedExisting?: boolean }).reusedExisting) {
        toast.success(`Matched existing customer "${selected.name}" and reused record`);
      } else {
        toast.success(`Customer "${selected.name}" created`);
      }
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  // ── Dropdown close on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Drag handlers ────────────────────────────────────────────────────────
  const handleCellMouseDown = useCallback((dayIdx: number, slotIdx: number) => {
    const day = weekDays[dayIdx];
    if (isPastSingaporeDay(day)) return; // no past dates
    setDragStart({ dayIdx, slotIdx });
    setDragEnd({ dayIdx, slotIdx });
  }, [weekDays, todayKey]);

  const handleCellMouseEnter = useCallback((dayIdx: number, slotIdx: number) => {
    if (isDragging && dragStart!.dayIdx === dayIdx) {
      setDragEnd({ dayIdx, slotIdx });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    if (dragStart && dragEnd) {
      const day = weekDays[dragStart.dayIdx];
      const slotMin = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
      const slotMax = Math.max(dragStart.slotIdx, dragEnd.slotIdx);
      setSelectedDay(day);
      setSelectedSlotStart(slotMin);
      setSelectedSlotEnd(slotMax);
      setSelectedAssignees([]);
      setScopeOfWork("");
      setVisitType("service_visit");
      setPreAgreedChargeEnabled(false);
      setPreAgreedChargeAmount("");
      setModalOpen(true);
    }
    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, weekDays]);

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseUp]);

  const isCellHighlighted = useCallback((dayIdx: number, slotIdx: number) => {
    if (!dragStart || !dragEnd) return false;
    if (dayIdx !== dragStart.dayIdx) return false;
    const minSlot = Math.min(dragStart.slotIdx, dragEnd.slotIdx);
    const maxSlot = Math.max(dragStart.slotIdx, dragEnd.slotIdx);
    return slotIdx >= minSlot && slotIdx <= maxSlot;
  }, [dragStart, dragEnd]);

  // ── Mobile two-tap state ─────────────────────────────────────────────────
  // Phase: null = idle, "first" = start slot tapped, waiting for end slot
  const [tapPhase, setTapPhase] = useState<null | "first">(null);
  const [tapStartSlot, setTapStartSlot] = useState<{ dayIdx: number; slotIdx: number } | null>(null);

  const cancelTap = useCallback(() => {
    setTapPhase(null);
    setTapStartSlot(null);
  }, []);

  // Navigate by viewDays steps
  const navPrev = () => { setCurrentDate(d => addDays(d, -viewDays)); cancelTap(); };
  const navNext = () => { setCurrentDate(d => addDays(d, viewDays)); cancelTap(); };
  const navToday = () => { setCurrentDate(new Date()); cancelTap(); };

  // Mobile tap highlight helpers
  const isCellTapStart = useCallback((dayIdx: number, slotIdx: number) => {
    return tapPhase === "first" && tapStartSlot?.dayIdx === dayIdx && tapStartSlot?.slotIdx === slotIdx;
  }, [tapPhase, tapStartSlot]);

  const isCellTapHint = useCallback((dayIdx: number, slotIdx: number) => {
    return tapPhase === "first" && tapStartSlot?.dayIdx === dayIdx && slotIdx > (tapStartSlot?.slotIdx ?? -1);
  }, [tapPhase, tapStartSlot]);

  // ── Mobile two-tap handler ───────────────────────────────────────────────
  const handleCellTap = useCallback((dayIdx: number, slotIdx: number) => {
    const day = weekDays[dayIdx];
    if (isPastSingaporeDay(day)) return;

    if (tapPhase === null) {
      // First tap — highlight start slot
      setTapStartSlot({ dayIdx, slotIdx });
      setTapPhase("first");
    } else {
      // Second tap
      if (tapStartSlot && tapStartSlot.dayIdx === dayIdx && slotIdx >= tapStartSlot.slotIdx) {
        // Valid second tap on same day, same or later slot
        setSelectedDay(weekDays[tapStartSlot.dayIdx]);
        setSelectedSlotStart(tapStartSlot.slotIdx);
        setSelectedSlotEnd(slotIdx);
        setSelectedAssignees([]);
        setScopeOfWork("");
        setVisitType("service_visit");
        setPreAgreedChargeEnabled(false);
        setPreAgreedChargeAmount("");
        setTapStartSlot(null);
        setTapPhase(null);
        setModalOpen(true);
      } else {
        // Different day or earlier slot — restart as new first tap
        setTapStartSlot({ dayIdx, slotIdx });
        setTapPhase("first");
      }
    }
  }, [tapPhase, tapStartSlot, weekDays, todayKey]);

  // ── Conflict detection ───────────────────────────────────────────────────────────────
  const conflictingVisits = useMemo(() => {
    if (!selectedDay || !modalOpen) return [];
    const conflicts: Array<{ userName: string; visitNumber: string }> = [];
    for (const v of existingVisits) {
      const placement = resolveSiteVisitCalendarPlacement(v);
      if (!placement || !selectedDay || !singaporeDateKeysEqual(placement.placementDate, selectedDay)) continue;
      if (!slotRangesOverlap(selectedSlotStart, selectedSlotEnd, placement.startSlot, placement.endSlot)) continue;
      const assignees = (v as any).assignees ?? [];
      for (const a of assignees) {
        if (selectedAssignees.some((sa) => sa.userId === a.userId)) {
          conflicts.push({ userName: a.userName, visitNumber: v.visitNumber });
        }
      }
    }
    return conflicts;
  }, [selectedDay, selectedSlotStart, selectedSlotEnd, selectedAssignees, existingVisits, modalOpen]);

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    if (!selectedCustomer) { toast.error("Please select a customer first"); return; }
    if (!selectedDay) return;

    const arrivalWindow = parseValidArrivalWindow(
      buildArrivalWindowFromSlots(selectedSlotStart, selectedSlotEnd),
    );
    if (!arrivalWindow) {
      toast.error("A scheduled visit requires a valid start and end time.");
      return;
    }

    if (preAgreedChargeEnabled && (!preAgreedChargeAmount || Number(preAgreedChargeAmount) <= 0)) {
      toast.error("Pre-agreed visit charge amount is required");
      return;
    }
    createVisit.mutate({
      customerId: selectedCustomer.id,
      siteId: selectedSiteId ?? undefined,
      visitType: "planned",
      visitDate: parseSingaporeDateInput(getSingaporeDateKeyFromInstant(selectedDay)!),
      arrivalWindow,
      complaintDescription: scopeOfWork || undefined,
      billable: false,
      customerInformed: false,
      assignees: selectedAssignees.length > 0 ? selectedAssignees : undefined,
      inventoryTeamRequested: false,
      inventoryTeamMessage: null,
      preAgreedChargeEnabled,
      preAgreedChargeAmount: preAgreedChargeEnabled ? Number(preAgreedChargeAmount) : undefined,
    });
  };

  const toggleAssignee = (userId: number, userName: string, role: Assignee["role"]) => {
    setSelectedAssignees(prev => {
      const exists = prev.some(a => a.userId === userId);
      if (exists) return prev.filter(a => a.userId !== userId);
      return [...prev, { userId, userName, role }];
    });
  };

  // Determine the address to display — use selected site if chosen, else primary
  const selectedSite = activeCustomerSites.find(s => s.id === selectedSiteId) ?? null;
  const fullAddress = selectedSite
    ? [selectedSite.unitNumber, selectedSite.address, selectedSite.postalCode].filter(Boolean).join(", ")
    : selectedCustomer
    ? [selectedCustomer.unitNumber, selectedCustomer.address, selectedCustomer.postalCode].filter(Boolean).join(", ")
    : "";

  // ── Selected time label ──────────────────────────────────────────────────
  const selectedTimeLabel = selectedDay && modalOpen
    ? `${formatSingaporeDate(selectedDay, { weekday: "short", day: "numeric", month: "short" })}, ${slotToTimeLabel(selectedSlotStart)} – ${slotToTimeLabel(selectedSlotEnd + 1)}`
    : "";

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-6 space-y-5 select-none">

        {/* ── Page header ── */}
        <div className="flex items-center gap-3">
          <button onClick={() => setLocation("/site-visits")} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-semibold">Schedule New Visit</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Select a customer, then drag on the calendar to pick a time slot</p>
          </div>
        </div>

        {/* ── Customer Details Card ── */}
        <div className="border rounded-lg bg-white p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Customer Details</h2>
          {selectedCustomer ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="font-semibold text-base">{selectedCustomer.name}</p>
                  {selectedCustomer.contact && <p className="text-sm text-muted-foreground">{selectedCustomer.contact}</p>}
                  {selectedCustomer.email && <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>}
                  {fullAddress && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />{fullAddress}
                    </p>
                  )}
                </div>
                <button onClick={() => { setSelectedCustomer(null); setCustomerQuery(""); setSelectedSiteId(null); }} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors shrink-0">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <ReportMissingSiteButton customerId={selectedCustomer.id} />
              {/* Site picker — only shown when customer has additional sites */}
              {activeCustomerSites.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Service Site</p>
                  <div className="flex flex-wrap gap-2">
                    {/* Primary address option */}
                    <button
                      onClick={() => setSelectedSiteId(null)}
                      className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                        selectedSiteId === null
                          ? "bg-black text-white border-black"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" />
                        {selectedCustomer.address
                          ? [selectedCustomer.unitNumber, selectedCustomer.address].filter(Boolean).join(" ")
                          : "Primary Address"}
                      </span>
                    </button>
                    {/* Additional site options */}
                    {activeCustomerSites.map((site) => (
                      <button
                        key={site.id}
                        onClick={() => setSelectedSiteId(site.id)}
                        className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                          selectedSiteId === site.id
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3" />
                          {site.label
                            ? `${site.label}${site.address ? ` — ${site.unitNumber ? site.unitNumber + " " : ""}${site.address}` : ""}`
                            : [site.unitNumber, site.address].filter(Boolean).join(" ") || `Site #${site.id}`}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    value={customerQuery}
                    onChange={(e) => { setCustomerQuery(e.target.value); setShowDropdown(true); }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search customer by name or contact number..."
                    className="w-full border border-gray-200 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors rounded-md"
                  />
                </div>
                {showDropdown && customerQuery.length >= 2 && (
                  <div className="absolute z-50 top-full mt-1 w-full bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto">
                    {searchResults && searchResults.length > 0 ? (
                      searchResults.map((c) => (
                        <button key={c.id} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 text-sm border-b last:border-b-0" onClick={() => { setSelectedCustomer(c); setCustomerQuery(c.name); setShowDropdown(false); }}>
                          <p className="font-medium">{c.name}</p>
                          {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2.5 text-sm text-muted-foreground">No customers found for &ldquo;{customerQuery}&rdquo;</div>
                    )}
                    <button
                      className="w-full text-left px-3 py-2.5 hover:bg-blue-50 text-sm flex items-center gap-2 text-blue-600 font-medium border-t"
                      onMouseDown={(e) => { e.preventDefault(); setShowDropdown(false); setNewCustForm(f => ({ ...f, name: customerQuery })); setShowNewCustomerModal(true); }}
                    >
                      <UserPlus className="h-4 w-4" />Create new customer &ldquo;{customerQuery}&rdquo;
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                New customer?{" "}
                <button className="text-blue-600 hover:underline" onClick={() => setShowNewCustomerModal(true)}>+ Add</button>
              </p>
            </div>
          )}
        </div>

          {/* ── Mobile tap hint banner ── */}
          {isMobile && tapPhase === "first" && tapStartSlot && (
            <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
              <span>
                Start: <strong>{slotToTimeLabel(tapStartSlot.slotIdx)}</strong> — now tap the end slot
              </span>
              <button onClick={cancelTap} className="p-1 rounded hover:bg-amber-100">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

        {/* ── Calendar ── */}
        <div className="border rounded-lg bg-white overflow-hidden">
          {/* Calendar header */}
          <div className="flex flex-col gap-2 px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={navToday}>Today</Button>
                <Button size="sm" variant="outline" onClick={navPrev}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-sm font-medium text-center">{weekTitle}</span>
                <Button size="sm" variant="outline" onClick={navNext}><ChevronRight className="h-4 w-4" /></Button>
              </div>
              {/* View toggle */}
              <div className="flex rounded-md border overflow-hidden text-xs">
                {([1, 3, 7] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setViewDays(v)}
                    className={`px-2.5 py-1 font-medium transition-colors ${
                      viewDays === v ? "bg-black text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {v === 1 ? "Day" : v === 3 ? "3 Days" : "Week"}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {isMobile
                ? tapPhase === "first"
                  ? "Now tap the end slot to set the duration"
                  : "Tap a slot to start, tap again to set end time"
                : "Drag to select a time slot"}
            </p>
          </div>

          {/* Day headers */}
          <div className="grid border-b bg-gray-50" style={{ gridTemplateColumns: `64px repeat(${viewDays}, 1fr)` }}>
            <div className="border-r" />
            {weekDays.map((day, i) => {
              const isToday = isTodaySingaporeDay(day);
              const isPast = isPastSingaporeDay(day);
              return (
                <div key={i} className={`py-2 text-center border-r last:border-r-0 ${isPast ? "opacity-40" : ""}`}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {day.toLocaleDateString("en-SG", { weekday: "short" })}
                  </div>
                  <div className={`mx-auto mt-0.5 w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold ${isToday ? "bg-black text-white" : "text-gray-800"}`}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time grid — scrollable */}
          <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
            <div className="grid" style={{ gridTemplateColumns: `64px repeat(${viewDays}, 1fr)` }}>
              <div className="border-r">
                {Array.from({ length: TOTAL_SLOTS }, (_, slotIdx) => (
                  <div key={slotIdx} className="border-b last:border-b-0 flex items-start justify-end pr-2 pt-0.5" style={{ height: SLOT_ROW_PX }}>
                    {slotIdx % 2 === 0 ? (
                      <span className="text-[10px] text-muted-foreground leading-none">{slotLabel(slotIdx)}</span>
                    ) : null}
                  </div>
                ))}
              </div>

              {weekDays.map((day, dayIdx) => {
                const isPast = isPastSingaporeDay(day);
                const isWeekend = dayIdx >= 5;
                const dayVisits = visitsByDay[dayIdx] ?? [];
                const dayLeaveBlocks = leaveBlocksByDay[dayIdx] ?? [];
                return (
                  <div
                    key={dayIdx}
                    className={`relative border-r last:border-r-0 ${isWeekend ? "bg-gray-50/40" : ""}`}
                    style={{ height: TOTAL_SLOTS * SLOT_ROW_PX }}
                  >
                    {Array.from({ length: TOTAL_SLOTS }, (_, slotIdx) => {
                      const isHighlighted = isCellHighlighted(dayIdx, slotIdx);
                      return (
                        <div
                          key={slotIdx}
                          onMouseDown={() => !isMobile && !isPast && handleCellMouseDown(dayIdx, slotIdx)}
                          onMouseEnter={() => !isMobile && handleCellMouseEnter(dayIdx, slotIdx)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            if (isMobile && !isPast) handleCellTap(dayIdx, slotIdx);
                          }}
                          className={`absolute left-0 right-0 border-b transition-colors ${
                            isHighlighted
                              ? "bg-amber-100"
                              : isCellTapStart(dayIdx, slotIdx)
                                ? "bg-amber-300"
                                : isCellTapHint(dayIdx, slotIdx)
                                  ? "bg-amber-50"
                                  : isPast
                                    ? "bg-gray-50/60 cursor-not-allowed"
                                    : "cursor-pointer hover:bg-amber-50/60"
                          }`}
                          style={{ top: slotIdx * SLOT_ROW_PX, height: SLOT_ROW_PX }}
                        />
                      );
                    })}
                    {dayLeaveBlocks.map((block) => {
                      const range = leavePeriodSlotRange(block.period as "full_day" | "am" | "pm");
                      return (
                        <div
                          key={`leave-${block.id}`}
                          className="pointer-events-none absolute inset-x-0.5 z-[5] rounded border border-rose-200 bg-rose-100/80 px-1 py-0.5 text-[10px] font-medium text-rose-800"
                          style={{
                            top: range.startSlot * SLOT_ROW_PX + 2,
                            height: Math.max((range.endSlot - range.startSlot + 1) * SLOT_ROW_PX - 4, SLOT_ROW_PX * 0.5),
                          }}
                          title={`${block.userName ?? `User ${block.userId}`} on leave (${block.period})${block.notes ? `: ${block.notes}` : ""}`}
                        >
                          <div className="truncate">{(block.userName ?? `User ${block.userId}`).split(" ")[0]} on leave</div>
                        </div>
                      );
                    })}
                    {dayVisits.map((visit) => {
                      const inactive = visit.status === "closed" || visit.status === "waived";
                      return (
                        <button
                          key={visit.id}
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/site-visits/id/${visit.id}`, { state: { from: pathname } });
                          }}
                          className={`absolute inset-x-0.5 z-10 overflow-hidden rounded border px-1 py-0.5 text-left text-[10px] hover:opacity-80 ${
                            STATUS_COLORS[visit.status] ?? "bg-gray-100 border-gray-300 text-gray-700"
                          } ${inactive ? "opacity-60" : ""}`}
                          style={{
                            top: visit.placement.startSlot * SLOT_ROW_PX + 2,
                            height: Math.max(
                              (visit.placement.endSlot - visit.placement.startSlot + 1) * SLOT_ROW_PX - 4,
                              SLOT_ROW_PX * 0.5
                            ),
                          }}
                        >
                          <div className="truncate font-semibold leading-tight">{visit.visitNumber}</div>
                          <div className="truncate leading-tight opacity-80">{(visit as any).customerName}</div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Submit for Approval ── */}
        <div className="pb-4">
          <Button
            size="lg"
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold text-base h-14 rounded-lg"
            disabled={!selectedCustomer}
            onClick={() => {
              if (!selectedCustomer) toast.error("Please select a customer and drag a time slot on the calendar");
              else toast.info("Drag a time slot on the calendar to schedule the visit");
            }}
          >
            Submit for Approval →
          </Button>
          {!selectedCustomer && (
            <p className="text-xs text-center text-muted-foreground mt-2">Select a customer above, then drag a time slot on the calendar</p>
          )}
        </div>
      </div>

      {/* ── Schedule Visit Modal ── */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) setModalOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Visit</DialogTitle>
            {selectedTimeLabel && (
              <p className="text-sm text-muted-foreground mt-1">{selectedTimeLabel}</p>
            )}
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Visit Type */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Visit Type</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setVisitType("new_install")}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md border transition-colors ${visitType === "new_install" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-700 border-gray-200 hover:border-amber-300"}`}
                >
                  New Install
                </button>
                <button
                  onClick={() => setVisitType("service_visit")}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md border transition-colors ${visitType === "service_visit" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-gray-700 border-gray-200 hover:border-amber-300"}`}
                >
                  Service Visit
                </button>
              </div>
            </div>

            {/* Assign Team */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Assign Team</p>
              <div className="space-y-4">
                {/* Technicians */}
                {technicians.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">TECHNICIANS</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {technicians.map((u) => {
                        const checked = selectedAssignees.some(a => a.userId === u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAssignee(u.id, u.name ?? "", "technician")}
                              className="w-4 h-4 rounded accent-amber-500"
                            />
                            <span className="text-sm">{u.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Programmers */}
                {programmers.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">PROGRAMMERS</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {programmers.map((u) => {
                        const checked = selectedAssignees.some(a => a.userId === u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAssignee(u.id, u.name ?? "", "programmer")}
                              className="w-4 h-4 rounded accent-amber-500"
                            />
                            <span className="text-sm">{u.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Installers */}
                {installers.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">INSTALLERS</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {installers.map((u) => {
                        const checked = selectedAssignees.some(a => a.userId === u.id);
                        return (
                          <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAssignee(u.id, u.name ?? "", "installer")}
                              className="w-4 h-4 rounded accent-amber-500"
                            />
                            <span className="text-sm">{u.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {technicians.length === 0 && programmers.length === 0 && installers.length === 0 && (
                  <p className="text-sm text-muted-foreground">No staff available. Add staff in Settings → Staff &amp; Users.</p>
                )}
              </div>
            </div>

            {/* Conflict warnings */}
            {conflictingVisits.length > 0 && (
              <div className="space-y-2">
                {conflictingVisits.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <span className="text-amber-500 text-sm mt-0.5">⚠</span>
                    <div className="flex-1 text-xs text-amber-800">
                      <span className="font-medium">{c.userName}</span> has a conflicting schedule at this time ({c.visitNumber}).{" "}
                      <button className="underline text-amber-700 font-medium" onClick={() => {
                        const id = existingVisits.find(v => v.visitNumber === c.visitNumber)?.id;
                        if (id != null) navigate(`/site-visits/id/${id}`, { state: { from: pathname } });
                      }}>
                        I am aware →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Scope of Work */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Scope of Work</p>
              <textarea
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                placeholder="Describe the scope of work for this visit..."
                rows={3}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md resize-none"
              />
            </div>

            {/* Pre-agreed Visit Charge */}
            <div className="space-y-2 rounded-md border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={preAgreedChargeEnabled}
                  onChange={(e) => {
                    setPreAgreedChargeEnabled(e.target.checked);
                    if (!e.target.checked) setPreAgreedChargeAmount("");
                  }}
                  className="h-4 w-4 rounded accent-amber-500"
                />
                Pre-agreed Visit Charge
              </label>
              {preAgreedChargeEnabled && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Amount (required)</p>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={preAgreedChargeAmount}
                    onChange={(e) => setPreAgreedChargeAmount(e.target.value)}
                    placeholder="e.g. 120.00"
                    className="w-full border border-gray-200 px-3 py-2 text-sm rounded-md focus:outline-none focus:border-black"
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={createVisit.isPending || !selectedCustomer}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {createVisit.isPending ? "Scheduling…" : "Confirm Slot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Inline Create New Customer Modal ── */}
      <Dialog open={showNewCustomerModal} onOpenChange={(open) => { if (!open) { setShowNewCustomerModal(false); setNewCustForm({ name: "", contact: "", email: "", address: "", unitNumber: "", postalCode: "", notes: "", isCondo: false }); setNewCustAddressQuery(""); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" />Create New Customer</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newCustForm.name.trim()) return;
              createCustomerInline.mutate({
                name: newCustForm.name.trim(),
                contact: newCustForm.contact.trim() || undefined,
                email: newCustForm.email.trim() || undefined,
                address: newCustForm.address.trim() || undefined,
                unitNumber: newCustForm.unitNumber.trim() || undefined,
                postalCode: newCustForm.postalCode.trim() || undefined,
                notes: newCustForm.notes.trim() || undefined,
                creationSource: "SERVICE_VISIT",
              });
            }}
            className="space-y-4 mt-2"
          >
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Full Name / Company Name <span className="text-red-500">*</span></label>
              <input type="text" value={newCustForm.name} onChange={(e) => setNewCustForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Chewbacca Chan" required className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Contact Number</label>
                <input type="text" value={newCustForm.contact} onChange={(e) => setNewCustForm(f => ({ ...f, contact: e.target.value }))} placeholder="e.g. 91234567" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Email Address</label>
                <input type="email" value={newCustForm.email} onChange={(e) => setNewCustForm(f => ({ ...f, email: e.target.value }))} placeholder="e.g. chan@email.com" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md" />
              </div>
            </div>
            <AddressSearchInput
              value={newCustAddressQuery}
              onAddressSelect={(address, postalCode) => { setNewCustForm(f => ({ ...f, address, postalCode })); setNewCustAddressQuery(address); }}
            />
            {newCustForm.address && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Is this a condo or apartment building?</label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setNewCustForm(f => ({ ...f, isCondo: true }))} className={`flex-1 px-3 py-2 text-sm border rounded-md transition-colors ${newCustForm.isCondo ? "border-black bg-black text-white" : "border-gray-200 text-gray-700 hover:border-black"}`}>Yes</button>
                  <button type="button" onClick={() => setNewCustForm(f => ({ ...f, isCondo: false }))} className={`flex-1 px-3 py-2 text-sm border rounded-md transition-colors ${!newCustForm.isCondo && newCustForm.address ? "border-black bg-black text-white" : "border-gray-200 text-gray-700 hover:border-black"}`}>No</button>
                </div>
              </div>
            )}
            {newCustForm.isCondo && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Unit / Floor</label>
                <input type="text" value={newCustForm.unitNumber} onChange={(e) => setNewCustForm(f => ({ ...f, unitNumber: e.target.value }))} placeholder="e.g. #03-12" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">Notes</label>
              <textarea value={newCustForm.notes} onChange={(e) => setNewCustForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes about this customer..." rows={2} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-black transition-colors rounded-md resize-none" />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowNewCustomerModal(false); }}>Cancel</Button>
              <Button type="submit" disabled={createCustomerInline.isPending || !newCustForm.name.trim()} className="bg-black text-white hover:bg-gray-900">
                {createCustomerInline.isPending ? "Creating…" : "Create & Select Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
